<template lang="pug">
    section.bookings-not-allowed(v-if="disabled")
        el-alert(type="info" :title="$t('Bookings are disabled for this event!')" :closable="false")
    section.bookings(v-else)
        paginated-items(
            :scope="scope"
            :type="key"
            :items="paginatedBookings"
            :show-filters="showFilters"
            :filters="pagination.filters"
            :variables="pagination.variables"
            :all-columns="allColumns"
            :active-columns="activeColumns"
            :page-info="pagination.pageInfo"
            @click="openBookingModal"
            @change-filter="changeFilter"
            @show-table-columns="changeTableColumns"
            @pagination-size-change="changePaginationSize"
            @pagination-current-change="changeCurrentPage"
        )
            div(slot="top")
                el-row.table-top(type="flex" justify="space-between")
                    .left
                        el-button(
                            :disabled="isEventOptionDisabled(form)"
                            type="primary"
                            size="mini"
                            icon="el-icon-fa-plus fas"
                            @click="openBookingModal"
                        )
                            | {{ $t('New booking') }}
                    .right
                        el-button(
                            plain
                            size="mini"
                            type="primary"
                            icon="el-icon-fa-filter fas"
                            @click="toggleFilterVisiblity"
                            :disabled="disable.showFilters"
                        )
                            span(v-if="showFilters") {{ $t('Hide filters') }}
                            span(v-else) {{ $t('Show filters') }}
            template(v-slot:booker:prefix="{ item }")
                span.will-be-notified(v-if="item.notify")
                    i.el-icon-fa-paper-plane.fas
            template(v-slot:actions="{ item }")
                el-button.delete-button(
                    v-if="! item.deleted"
                    plain
                    circle
                    size="mini"
                    type="danger"
                    icon="el-icon-fa-trash-alt far"
                    @click.stop.prevent="deleteBooking(item)"
                )
                el-button.restore-button(
                    v-else
                    plain
                    circle
                    size="mini"
                    type="success"
                    icon="el-icon-fa-trash-restore fas"
                    @click.stop.prevent="restoreBooking(item)"
                )
        el-dialog(
            :title="modalTitle"
            :visible.sync="modalVisibility"
            :close-on-press-escape="false"
            :close-on-click-modal="false"
            top="10vh"
            width="720px"
            @closed="resetModal"
        )
            el-form.booking-modal-form(
                :model="modal"
                label-position="top"
                :rules="rules"
                ref="modal"
                v-loading="modalLoading"
                @submit.prevent.native="submitModalForm"
            )
                el-row.no-padding(:gutter="20")
                    el-col(:lg="6" :md="6")
                        el-form-item(:label="$t('Salutation')" prop="booker.gender")
                            el-select.is-full-width(placeholder="" v-model="modal.booker.gender")
                                el-option(:label="$t('Mr.')" value="M")
                                el-option(:label="$t('Mrs.')" value="F")
                                el-option(:label="$t('Undefined')" value="X")
                    el-col(:lg="9" :md="9")
                        el-form-item(:label="$t('First name')" prop="booker.first_name")
                            el-input(v-model="modal.booker.first_name")
                    el-col(:lg="9" :md="9")
                        el-form-item(:label="$t('Last name')" prop="booker.last_name")
                            el-input(v-model="modal.booker.last_name")
                el-row.no-padding(:gutter="20")
                    el-col(:lg="12" :md="12")
                        el-form-item(:label="$t('Email')" prop="booker.email")
                            el-input(v-model="modal.booker.email")
                    el-col(:lg="12" :md="12")
                        el-form-item(:label="$t('Phone')" prop="booker.phone")
                            el-input(v-model="modal.booker.phone")
                el-row
                    el-form-item(:label="$t('Event')" prop="event")
                        el-select.is-full-width(placeholder="" v-model="modal.event" valueKey="id" disabled)
                            el-option(
                                v-for="event in options.event.items"
                                :disabled="isEventOptionDisabled(event)"
                                :key="event.id"
                                :label="getEventLabel(event)"
                                :value="event"
                            )
                    el-form-item(v-if="modal.event" :label="$t('Slot')" prop="slot")
                        el-select.is-full-width(placeholder="" v-model="modal.slot" valueKey="id")
                            el-option(
                                v-for="slot in slotOptions"
                                :disabled="isSlotOptionDisabled(slot)"
                                :key="slot.id"
                                :label="getSlotLabel(slot)"
                                :value="slot"
                            )
            template(booking="footer")
                .modal-footer
                    .left
                        .switch(v-if="modalType !== 'existing'")
                            el-switch(
                                :active-text="$t('Send email')"
                                v-model="modal.notify"
                            )
                    .right
                        el-button(
                            size="mini"
                            icon="el-icon-fa-times fas"
                            @click="closeModal"
                        ) {{ $t('Dismiss') }}
                        el-button(
                            :disabled="modalLoading"
                            size="mini"
                            type="primary"
                            icon="el-icon-fa-check fas"
                            @click="submitModalForm"
                        ) {{ $t('Ok') }}
</template>

<i18n>
{
  "de": {
    "New booking": "Neue Anmeldung",
    "Edit booking": "Booking bearbeiten",
    "Bookings are disabled for this event!": "Anmeldungen sind für dieses Event deaktiviert!",
    "Start": "Start",
    "End": "Ende",
    "Comment": "Kommentar",
    "Show filters": "Filter anzeigen",
    "Hide filters": "Filter verbergen",
    "Salutation": "Anrede",
    "First name": "Vorname",
    "Last name": "Nachname",
    "Email": "E-Mail",
    "Phone": "Telefon",
    "Company": "Firma",
    "Mr.": "Herr",
    "Mrs.": "Frau",
    "Undefined": "Unbekannt",
    "Send email": "E-Mail versenden"
  }
}
</i18n>
<script>

import _ from 'lodash'
import Vue from 'vue'
import uniqid from 'uniqid'

import { mapGetters, mapMutations, mapActions } from 'vuex'

import store from '@/store'
import quillConfig from '@/quill/config'
import { timestamp2date } from  '@/filters'

import PaginatedItems from '@/components/PaginatedItems'

export default {
    name: 'Bookings',
    data() {
        quillConfig.modules.link.pages = this.pageOptions
        return {
            scope: 'ex',
            key: 'evnt_event_booking',
            disable: {
                showFilters: false,
            },
            modalVisibility: false,
            modalLoading: false,
            modalType: 'new', // valid options: 'new' / fresh / 'existing'
            modal: {
                id: undefined,
                event: undefined,
                booker: {
                    id: undefined,
                    gender: 'M',
                    first_name: '',
                    last_name: '',
                    email: '',
                    phone: '',
                    company: ''
                },
                slot: undefined,
                notify: false
            },
            pagination: {
                filters: {},
                variables: {
                    paginationSizes: [5, 10, 50],
                },
                pageInfo: {
                    currentPage: 1,
                    lastPage: 1,
                    perPage: 10,
                    total: 0,
                }
            },
            quillConfig,
        }
    },
    components: {
        PaginatedItems,
    },
    watch: {
        bookings(bookings) {
            this.pagination.pageInfo.total = bookings.length
            this.pagination.pageInfo.lastPage = Math.ceil(bookings.length / this.pagination.pageInfo.perPage)
        }
    },
    computed: {
        bookings() {
            let bookings = []

            let slots = _.get(this.form, 'slots', [])
            slots.forEach(slot => {
                let slotBookings = _.cloneDeep(slot.bookings || [])
                _.map(slotBookings, booking => {
                    booking.slot = _.omit(slot, ['bookings'])
                })
                bookings = [...bookings, ...slotBookings]
            })

            // //  apply filters
            // bookings = bookings.filter(booking => {
            //     let match = true
            //
            //     if (this.pagination.filters.id) {
            //         const filter = this.pagination.filters.id
            //         if (match && filter.from) {
            //             match = booking.id >= filter.from
            //         }
            //         if (match && filter.to) {
            //             match = booking.id <= filter.to
            //         }
            //     }
            //
            //     if (match && this.pagination.filters.start_at) {
            //         const filter = this.pagination.filters.start_at
            //         return booking.start_at >= filter.from
            //             && booking.start_at <= filter.to
            //     }
            //
            //     if (match && this.pagination.filters.end_at) {
            //         const filter = this.pagination.filters.end_at
            //         return booking.end_at >= filter.from
            //             && booking.end_at <= filter.to
            //     }
            //
            //     if (match && this.pagination.filters.comment) {
            //         const filter = this.pagination.filters.comment
            //         const search = _.toLower(filter.like)
            //         match = _.toLower(booking.comment).indexOf(search) >= 0
            //     }
            //
            //     return match
            // })

            return bookings
        },
        paginatedBookings() {
            let bookings = this.bookings

            const index = this.pagination.pageInfo.currentPage - 1
            const start = this.pagination.pageInfo.perPage * index
            const end = start + this.pagination.pageInfo.perPage

            bookings = bookings.slice(start, end)
            return bookings
        },
        activeColumns() {
            let activeColumns = [];

            const activeColumnKeys = _.get(this.user.settings, `${this.scope}.pagination.${this.key}.activeColumnKeys`, [])
            _.each(this.allColumns, column => {
                const allowed = _.find(activeColumnKeys, obj => column.key === obj.key);
                if (allowed) activeColumns.push(column);
            });

            if (! activeColumns.length) {
                activeColumns = this.allColumns.filter(column => column.default === true)
            }

            return activeColumns
        },
        showFilters() {
            return _.get(this.user.settings, `${this.scope}.pagination.${this.key}.showFilters`) || false
        },
        allColumns() {
            const filterOptionsFromTo = [
                {
                    key: 'from',
                    placeholder: 'von',
                    position: 10,
                },
                {
                    key: 'to',
                    placeholder: 'bis',
                    position: 20,
                }
            ]

            const columns = [
                {
                    key: 'id',
                    name: 'ID',
                    value_path: 'id',
                    filter: {
                        type: 'NumberFromTo',
                        options: filterOptionsFromTo
                    },
                    default: false,
                },
                {
                    key: 'booker',
                    name: 'Person',
                    value_path: 'booker.name',
                    filter: {
                        type: 'SingleInput',
                    },
                    default: true,
                },
                {
                    key: 'email',
                    name: 'E-Mail',
                    value_path: 'booker.email',
                    filter: {
                        type: 'SingleInput',
                    },
                    default: true,
                },
                {
                    key: 'start_at',
                    name: 'Start',
                    value_path: 'slot.start_at',
                    filter: {
                        type: 'DateTimeFromTo',
                        options: filterOptionsFromTo
                    },
                    default: true,
                },
                {
                    key: 'end_at',
                    name: 'Ende',
                    value_path: 'slot.end_at',
                    filter: {
                        type: 'DateTimeFromTo',
                        options: filterOptionsFromTo
                    },
                    default: true,
                },
                {
                    key: 'created_at',
                    name: 'erstellt am',
                    value_path: 'created_at',
                    filter: {
                        type: 'DateFromTo',
                        options: filterOptionsFromTo
                    },
                    default: false,
                },
                {
                    key: 'updated_at',
                    name: 'zuletzt bearbeitet',
                    value_path: 'updated_at',
                    filter: {
                        type: 'DateFromTo',
                        options: filterOptionsFromTo
                    },
                    default: false,
                },
            ]

            return columns
        },
        modalTitle() {
            let title = this.$t('New booking')
            if (this.modalType === 'existing') {
                title = this.$t('Edit booking')
            }
            return title
        },
        rules() {
            let rules = {
                event: 'required',
                booker: {
                    first_name: 'required',
                    last_name: 'required',
                    email: 'required|email'
                },
                slot: 'required'
            }

            // add booker.email exclude rule

            if (this.modal.slot) {
                const bookings = this.bookings.filter(booking => booking.slot.id === this.modal.slot.id)
                let excludedEmails = []
                bookings.forEach(booking => {
                    if (this.modalType !== 'existing' || this.modal.id !== booking.id)
                        excludedEmails.push(booking.booker.email)
                })

                if (excludedEmails.length) rules.booker.email += `|excluded:${excludedEmails.join(',')}`
            }

            return rules
        },
        slotOptions() {
            let options = []

            if (this.modal.event) {
                const events = _.get(this.options, 'event.items') || []
                let event = events.find(event => event.id === this.modal.event.id)
                if (event.id === this.form.id) event = this.form

                if (_.get(event, 'slots.length')) options = event.slots
            }

            return options
        },
        disabled() {
            let disabled = true

            let bookings = []
            let slots = _.get(this.form, 'slots', [])
            slots.forEach(slot => {
                let slotBookings = slot.bookings || []
                bookings = [...bookings, ...slotBookings]
            })

            const booking = _.get(this.form, 'booking') || 'none'
            if (booking !== 'none' || bookings.length) {
                disabled = false
            }

            return disabled
        },
        ...mapGetters({
            form: 'ext_eventManager_event/form',
            options: 'ext_eventManager_event/options',
            user: 'auth/user',
        }),
    },
    methods: {
        toggleFilterVisiblity() {
            const visible = this.showFilters

            let value = false
            if (! visible) value = true

            this.disable.showFilters = true
            this.saveFilterVisibility({ scope: this.scope, key: this.key, value })
                .finally(() => this.disable.showFilters = false)
        },
        changeFilter(key, value) {
            if (value.match === '' && value.like === '' && value.from === '' && value.to === '' && value.in.length === 0) {
                Vue.delete(this.pagination.filters, key)
            } else {
                Vue.set(this.pagination.filters, key, value)
            }

            this.pagination.pageInfo.currentPage = 1
            this.pagination.pageInfo.total = this.bookings.length
            this.pagination.pageInfo.lastPage = Math.ceil(this.bookings.length / this.pagination.pageInfo.perPage)
        },
        changeTableColumns(visibleColumns) {

            let activeColumnKeys = []
            this.allColumns.forEach(column => {
                const visible = _.find(visibleColumns, value => column.key === value)
                if (visible) {
                    activeColumnKeys.push({ key: column.key })
                    column.default = true
                } else {
                    column.default = false
                }
            })

            let settings = _.get(this.user, 'settings') || {}
            settings = _.cloneDeep(settings)

            _.set(settings, `${this.scope}.pagination.${this.key}.activeColumnKeys`, activeColumnKeys)
            store.commit('auth/updateUserSettings', settings)
        },
        changePaginationSize(value) {
            this.pagination.pageInfo.currentPage = 1
            this.pagination.pageInfo.perPage = value
        },
        changeCurrentPage(value) {
            this.pagination.pageInfo.currentPage = value
        },
        openBookingModal(item = null) {
            this.modalLoading = true
            this.modalVisibility = true

            if (item && item instanceof Event === false) {
                this.modal = _.cloneDeep(item)

                // find event option
                let id = _.get(this.modal, 'slot.event.id')
                if (id) {
                    this.modal.event = this.options.event.items.find(event => event.id === id)
                } else if (!this.isEventOptionDisabled(this.form)) {
                    this.modal.event = this.form
                }

                if (item && _.startsWith(item.id, 'new:')) {
                    this.modalType = 'fresh'
                } else {
                    this.modalType = 'existing'
                }
            } else {
                this.modal.id = uniqid('new:')
                this.modalType = 'new'

                if (!this.isEventOptionDisabled(this.form)) {
                    this.modal.event = this.form
                }

                // const startAt = startOfHour(addDays(new Date(), 1))
                // this.modal.start_at = getUnixTime(startAt)
                // this.modal.end_at = getUnixTime(addHours(startAt, 1))
            }

            // is necessary for update
            this.modal.originalSlot = this.modal.slot

            this.modalLoading = false
        },
        closeModal() {
            this.modalVisibility = false
            this.modalLoading = false
        },
        submitModalForm() {
            this.$refs.modal.validate(valid => {
                if (valid) {
                    if (this.modalType === 'new') {
                        // add user to team
                        this.addBooking(this.modal)
                        this.closeModal()
                    } else {
                        // replace administrators in team-user
                        this.updateBooking(this.modal)
                        this.closeModal()
                    }
                }
            })
        },
        resetModal() {
            this.modal = {
                id: undefined,
                event: undefined,
                booker: {
                    id: undefined,
                    gender: 'M',
                    first_name: '',
                    last_name: '',
                    email: '',
                    phone: '',
                    company: ''
                },
                slot: undefined,
                notify: false
            }
            this.$refs.modal.resetFields()
        },
        getEventLabel(event) {
            let label = ''
            if (_.get(event, 'name')) label = event.name
            if (_.get(event, 'category.name')) label += ` - ${event.category.name}`
            return label
        },
        isEventOptionDisabled(event) {
            let disabled = false

            if (event.booking === 'none') disabled = true
            else if (!event.slots.length) disabled = true
            else if (
                event.member_limit_per_slot !== null
                && !event.slots.filter(slot => _.get(slot, 'bookings.length', 0) >= event.member_limit_per_slot)
            ) disabled = true

            return disabled
        },
        getSlotLabel(slot) {
            let label = ''

            if (slot.start_date === slot.end_date) {
                label = `${timestamp2date(slot.start_at)}, ${timestamp2date(slot.start_at, 'HH:mm')} - ${timestamp2date(slot.end_at, 'HH:mm')} Uhr`
            } else {
                label = `${timestamp2date(slot.start_at)}, ${timestamp2date(slot.start_at, 'HH:mm')} - ${timestamp2date(slot.end_at)}, ${timestamp2date(slot.end_at, 'HH:mm')}`
            }

            return label
        },
        isSlotOptionDisabled(slot) {
            let disabled = false

            // check in event if the current slot has capacity left

            const bookings = slot.bookings || []
            if (this.modal.event.booking === 'none') disabled = true
            else if (
                this.modal.event.member_limit_per_slot !== null
                && bookings.filter(booking => booking.id !== this.modal.id).length >= this.modal.event.member_limit_per_slot
            ) disabled = true

            return disabled
        },

        ...mapMutations({
            deleteBooking: 'ext_eventManager_event/form/markBookingAsDeleted',
            restoreBooking: 'ext_eventManager_event/form/unmarkBookingAsDeleted',
            addBooking: 'ext_eventManager_event/form/addBooking',
            updateBooking: 'ext_eventManager_event/form/updateBooking',
        }),
        ...mapActions({
            saveFilterVisibility: 'saveFilterVisibility',
        }),
    },
    mounted() {
        this.pagination.pageInfo.total = this.bookings.length
        this.pagination.pageInfo.lastPage = Math.ceil(this.bookings.length / this.pagination.pageInfo.perPage)
    }
}

</script>

<style lang="sass" scoped>
    .event
        .el-card__body
            & > section.bookings
                padding: 0
                ::v-deep
                    .column.booker
                        position: relative
                        .will-be-notified
                            position: absolute
                            left: -15px
                            top:  0
                            font-size: 11px
                            color: $--color-info

    .booking-modal-form
    .el-form > .el-form-item:last-child
        margin-bottom: 0
    .modal-footer
        display: flex
        justify-content: space-between
        .left
            display: flex
            align-items: center
            .switch
                height: 28px
                line-height: 28px

    // .el-date-editor.el-range-editor
    //     width: 100%
    //     ::v-deep
    //         input.el-range-input
    //             text-align: left
    //             padding-left: 4px

    .from-to-grid
        display: grid
        grid-template-columns: auto auto
        gap: 1rem
</style>
